import { KPICard } from "@/components/KPICard";
import { PopularItem } from "@/components/PopularItem";
import { EmptyState } from "@/components/EmptyState";
import { DepartmentGoals } from "@/components/DepartmentGoals";
import { Sparkles, ShoppingBag, Scissors, Gift, DollarSign, Package } from "lucide-react";
import { t } from "@/i18n";

const formatDKK = (amount) =>
  new Intl.NumberFormat("da-DK", {
    style: "currency",
    currency: "DKK",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);

// --- I DAG: de 4 oversigtskort (Ydelser / Produkter / Gavekort / Samlet) ---
// Eksporteret så PerformanceView (Opf. #45) kan genbruge dem uden duplikering.
export function OverviewCards({ data, period, lang = "da" }) {
  const { overview, comparison_totals, prev_day_label } = data;

  const PERIOD_SUFFIX = {
    day: t(lang, "periodSuffixDay"),
    week: t(lang, "periodSuffixWeek"),
    month: t(lang, "periodSuffixMonth"),
    last_month: t(lang, "periodSuffixLastMonth"),
    prev_month: t(lang, "periodSuffixPrevMonth"),
  };

  const suffix = PERIOD_SUFFIX[period] || t(lang, "periodSuffixDay");
  const giftcardLabel = lang === "en" ? "Gift & Clip Cards" : "Gavekort og Klippekort";

  // Diff for total KPICard
  let diffAmount = null;
  let diffLabel = null;
  if (comparison_totals) {
    const cur = overview.total.amount;
    if (period === "day" && comparison_totals.prev_day != null && comparison_totals.prev_day > 0) {
      diffAmount = Math.round(cur - comparison_totals.prev_day);
      diffLabel = `sidste ${prev_day_label || "uge"}`;
    } else if (period === "week" && comparison_totals.prev_week != null && comparison_totals.prev_week > 0) {
      diffAmount = Math.round(cur - comparison_totals.prev_week);
      diffLabel = "sidste uge";
    } else if (period === "month" && comparison_totals.same_day_prev_month != null && comparison_totals.same_day_prev_month > 0) {
      diffAmount = Math.round(cur - comparison_totals.same_day_prev_month);
      diffLabel = "samme dato forrige måned";
    } else if (period === "last_month" && comparison_totals.prev_last_month != null && comparison_totals.prev_last_month > 0) {
      diffAmount = Math.round(cur - comparison_totals.prev_last_month);
      diffLabel = "måneden før";
    } else if (period === "prev_month" && comparison_totals.prev_prev_month != null && comparison_totals.prev_prev_month > 0) {
      diffAmount = Math.round(cur - comparison_totals.prev_prev_month);
      diffLabel = "måneden før";
    }
  }

  return (
    <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
      <KPICard
        label={`${t(lang, "kpiServices")} ${suffix}`}
        value={formatDKK(overview.services.amount)}
        subtitle={t(lang, "kpiServicesSub", overview.services.count)}
        icon={Scissors}
        delay={1}
      />
      <KPICard
        label={`${t(lang, "kpiProducts")} ${suffix}`}
        value={formatDKK(overview.products.amount)}
        subtitle={t(lang, "kpiProductsSub", overview.products.count)}
        icon={ShoppingBag}
        delay={2}
      />
      <KPICard
        label={`${giftcardLabel} ${suffix}`}
        value={formatDKK(overview.gavekort.amount)}
        subtitle={t(lang, "kpiGiftcardsSub", overview.gavekort.count)}
        icon={Gift}
        delay={3}
      />
      <KPICard
        label={`${t(lang, "kpiTotal")} ${suffix}`}
        value={formatDKK(overview.total.amount)}
        subtitle={t(lang, "kpiTotalSub", overview.total.count)}
        icon={DollarSign}
        delay={4}
        diffAmount={diffAmount}
        diffLabel={diffLabel}
      />
    </div>
  );
}

// --- POPULÆRE PRODUKTER / BEHANDLINGER ---
// Eksporteret til genbrug i PerformanceView (Opf. #45).
export function PopularSection({ data, lang = "da" }) {
  const { popular_treatments, popular_products } = data;
  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      <PopularItem
        title={t(lang, "popularProducts")}
        icon={Package}
        items={popular_products}
        accentColor="#00FF66"
        lang={lang}
      />
      <PopularItem
        title={t(lang, "popularTreatments")}
        icon={Sparkles}
        items={popular_treatments}
        accentColor="#FF2A6D"
        lang={lang}
      />
    </div>
  );
}

export const StaffView = ({ data, period, refreshing, lang = "da" }) => {
  const {
    overview, department_ranking,
    day_by_dept, week_by_dept, month_by_dept, last_month_by_dept, prev_month_by_dept,
    streak_by_dept, breakeven_streak_by_dept,
  } = data;

  const isEmpty = overview.total.count === 0;
  const monthData = period === "last_month" ? last_month_by_dept : period === "prev_month" ? prev_month_by_dept : month_by_dept;

  if (isEmpty) return <EmptyState lang={lang} />;

  return (
    <div className="space-y-6" data-testid="staff-view">
      <OverviewCards data={data} period={period} lang={lang} />

      <DepartmentGoals
        departmentRanking={department_ranking}
        dayData={day_by_dept}
        weekData={week_by_dept}
        monthData={monthData}
        period={period}
        lang={lang}
        streakByDept={streak_by_dept}
        breakevenStreakByDept={breakeven_streak_by_dept}
      />

      <PopularSection data={data} lang={lang} />
    </div>
  );
};
